// src/lib/crisp.ts
import { Crisp } from "crisp-sdk-web";

const WEBSITE_ID = process.env.REACT_APP_CRISP_WEBSITE_ID;

export function initCrisp ({ autoload = false } = {}) {
  // call once on client
  if (typeof window === "undefined") return;
  Crisp.configure(WEBSITE_ID, { autoload });

  // Hide the default Crisp chat widget on initialization
  Crisp.chat.hide();

  // Listen for when chat is closed and hide the widget
  Crisp.chat.onChatClosed(() => {
    Crisp.chat.hide();
  });

  // Set up message listeners - try multiple event patterns
  try {
    // Method 1: Try standard event listeners if available
    if (Crisp.chat.onMessageReceived) {
      Crisp.chat.onMessageReceived(() => {
        Crisp.chat.show();
      });
    }

    // Method 2: Try alternative event names
    if (Crisp.chat.onMessage) {
      Crisp.chat.onMessage(() => {
        Crisp.chat.show();
      });
    }

    // Method 3: Use push method for event listening (common Crisp pattern)
    if (!window.$crisp) {
      window.$crisp = [];
    }

    window.$crisp.push(['on', 'message:received', () => {
      Crisp.chat.show();
    }]);

    window.$crisp.push(['on', 'message:sent', () => {
      // When user sends a message, keep widget visible for potential replies
      // Don't hide it immediately
    }]);

    // Listen for chat opened to ensure widget stays visible during conversation
    window.$crisp.push(['on', 'chat:opened', () => {
      Crisp.chat.show();
    }]);

    // Method 4: Periodic check for unread messages (fallback)
    let lastMessageCount = 0;
    const checkForNewMessages = () => {
      try {
        const currentCount = Crisp.chat.getUnreadCount?.() || 0;
        if (currentCount > lastMessageCount) {
          Crisp.chat.show();
          lastMessageCount = currentCount;
        }
      } catch (error) {
        // Silently handle if method doesn't exist
      }
    };

    // Check every 5 seconds for new messages
    setInterval(checkForNewMessages, 5000);

  } catch (error) {
    console.warn('Error setting up Crisp message listeners:', error);
  }
}

// load chat manually (use when autoload: false)
export function loadCrisp () {
  if (typeof window === "undefined") return;
  Crisp.load();
}

// Smart session management - only reset if user has changed
const CURRENT_USER_KEY = 'crisp_current_user_email';

function getCurrentUserEmail () {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CURRENT_USER_KEY);
}

function setCurrentUserEmail (email) {
  if (typeof window === "undefined") return;
  if (email) {
    localStorage.setItem(CURRENT_USER_KEY, email);
  } else {
    localStorage.removeItem(CURRENT_USER_KEY);
  }
}

export function checkAndResetIfUserChanged (newUser) {
  if (!newUser || !newUser.email) {
    return;
  }

  try {
    const previousUserEmail = getCurrentUserEmail();

    // If this is a different user than before, reset the session
    if (previousUserEmail && previousUserEmail !== newUser.email) {
      forceResetForNewUser();
    }

    // Update current user tracking
    setCurrentUserEmail(newUser.email);
  } catch (error) {
    console.warn('Error checking/resetting Crisp session:', error);
  }
}

export function clearCurrentUser () {
  setCurrentUserEmail(null);
}

// identify logged-in user (call after login)
export function identifyUser (user) {
  if (!user) {
    // Clear user data when no user is provided
    try {
      Crisp.user.setEmail(null);
      Crisp.user.setNickname(null);
    } catch (error) {
      console.warn('Error clearing Crisp user data:', error);
    }
    return;
  }

  try {
    // Check if user has changed and reset session if needed
    checkAndResetIfUserChanged(user);

    if (user.email) Crisp.user.setEmail(user.email);
    if (user.displayName) Crisp.user.setNickname(user.displayName);
    if (user.uid) Crisp.session.setData({ user_id: user.uid });
  } catch (error) {
    console.warn('Error identifying user with Crisp:', error);
  }
}

// session continuity: optional (use securely)
export function setCrispToken (token) {
  if (token) Crisp.setTokenId(token); // persist session to token
  else Crisp.setTokenId(); // clears token
}

// open/close helpers
export const openChat = () => {
  Crisp.chat.show(); // Show the widget first
  Crisp.chat.open(); // Then open it
};
export const showChat = () => Crisp.chat.show();
export const hideChat = () => Crisp.chat.hide();
export const resetSession = () => {
  Crisp.session.reset();
  Crisp.chat.hide(); // Hide chat after reset
  clearCurrentUser(); // Clear user tracking
};

export const forceResetForNewUser = () => {
  try {
    // Multiple reset attempts to ensure clean slate
    Crisp.session.reset();
    Crisp.chat.hide();

    // Clear any stored session data
    if (Crisp.session.setData) {
      Crisp.session.setData({});
    }

    // Clear user data
    if (Crisp.user.setEmail) {
      Crisp.user.setEmail(null);
    }
    if (Crisp.user.setNickname) {
      Crisp.user.setNickname(null);
    }

    // Additional reset after a short delay
    setTimeout(() => {
      Crisp.session.reset();
      Crisp.chat.hide();
    }, 200);

  } catch (error) {
    console.warn('Error in force reset:', error);
  }
};